import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  TextField,
  Grid,
  Divider,
  useTheme,
  IconButton,
  Tooltip,
  Fade,
  alpha,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
  ContentCopy as CopyIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  LocalOffer as ServiceIcon,
  PersonOutline as StaffIcon,
  Schedule as ScheduleIcon,
  Notes as NotesIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useParams, useNavigate } from 'react-router-dom';
import { useManualBooking } from '../../../context/ManualBookingContext';
import GradientButton from '../../../../components/buttons/GradientButton';
import { sendShareableLinkEmail } from '../../../services/sendShareableLinkEmail';
import { confirmStaffManualBooking } from '../../../services/confirmStaffManualBooking';
import { createShortLink } from '../../../services/createShortLink';
import { useNotification } from '../../../../context/NotificationContext';
import { getCompanySlug } from '../../../../client-experience/utils/companyAccess';

// Format phone number for display: (XXX) XXX-XXXX
const EMAIL_COOLDOWN_MS = 5 * 60 * 1000; // 5 minutes
const SHORT_LINK_TIMEOUT_MS = 8000;

const withTimeout = (promise, ms) => (
  Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Short link request timed out')), ms);
    }),
  ])
);

const formatCooldown = (msLeft) => {
  if (msLeft <= 0) return null;
  const totalSeconds = Math.ceil(msLeft / 1000);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const calculateTotalDuration = (service) => {
  // If it's a package, use package duration
  if (service.selectedPackage?.duration) {
    const packageDuration = parseInt(service.selectedPackage.duration) || 0;
    const packageBufferTime = parseInt(service.selectedPackage.bufferTime) || 0;
    
    // Add durations from selected add-ons
    const addonsDuration = (service.selectedAddons || []).reduce((total, addon) => 
      total + (parseInt(addon.duration) || 0) + (parseInt(addon.bufferTime) || 0), 0
    );
    
    return packageDuration + packageBufferTime + addonsDuration;
  }

  // If not a package, use service duration
  const duration = parseInt(service.duration) || 60;
  const bufferTime = parseInt(service.bufferTime) || 0;
  const addonsDuration = (service.selectedAddons || []).reduce((total, addon) => 
    total + (parseInt(addon.duration) || 0) + (parseInt(addon.bufferTime) || 0), 0
  );
  
  return duration + bufferTime + addonsDuration;
};

const ConfirmationStep = ({ onBack, onSuccess }) => {
  const publicAppBaseUrl = 'https://neatschedule.com';
  const { showToast } = useNotification();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notes, setNotes] = useState('');
  const [shareableLink, setShareableLink] = useState('');
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const { bookingData, updateBookingData } = useManualBooking();

  const handleChangeNotes = (event) => {
    setNotes(event.target.value);
  };

  const buildStorefrontShareUrl = () => {
    const companySlug = getCompanySlug(bookingData.companyData);
    if (!companySlug) return null;
    return `${publicAppBaseUrl}/book/${companySlug}`;
  };

  const tryCreateShortLink = async (shortSlug, publicUrl) => {
    try {
      const data = await withTimeout(
        createShortLink({
          companyId: bookingData.companyData.id,
          targetUrl: publicUrl,
          kind: 'other',
          context: { shortSlug },
        }),
        SHORT_LINK_TIMEOUT_MS
      );
      return data?.shortUrl || publicUrl;
    } catch (e) {
      console.warn('[ConfirmationStep] Short link generation failed, using storefront URL', e);
      return publicUrl;
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareableLink);
      showToast('Link copied to clipboard!', 'success');
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailCooldownUntil, setEmailCooldownUntil] = useState(null);
  const [emailCooldownLabel, setEmailCooldownLabel] = useState('');

  const handleEmailLink = async () => {
    try {
      setSendingEmail(true);
      const result = await sendShareableLinkEmail({
        companyId: bookingData.companyData.id,
        companyName: bookingData.companyData.name,
        toEmail: bookingData.customer.email,
        toName: bookingData.customer.name,
        serviceName: bookingData.selectedService?.name,
        employeeName: bookingData.selectedEmployee?.name,
        dateTime: bookingData.selectedDateTime ?? bookingData.dateTime,
        shareUrl: shareableLink,
      });
      const until = result?.cooldownUntil ?? Date.now() + EMAIL_COOLDOWN_MS;
      setEmailCooldownUntil(until);
      showToast('Email sent to customer', 'success');
    } catch (e) {
      console.error('Failed to send email:', e);
      const msg = e?.message || 'Failed to send email';
      showToast(typeof msg === 'string' ? msg : 'Failed to send email', 'error');
      const until = e?.details?.cooldownUntil ?? (e?.code === 'functions/resource-exhausted' ? Date.now() + EMAIL_COOLDOWN_MS : null);
      if (until) setEmailCooldownUntil(until);
    } finally {
      setSendingEmail(false);
    }
  };

  useEffect(() => {
    if (emailCooldownUntil == null) return;
    const tick = () => {
      const now = Date.now();
      if (now >= emailCooldownUntil) {
        setEmailCooldownUntil(null);
        setEmailCooldownLabel('');
        return;
      }
      setEmailCooldownLabel(formatCooldown(emailCooldownUntil - now));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [emailCooldownUntil]);

  const handleConfirmBooking = async () => {
    if (!bookingData.selectedService || !bookingData.selectedEmployee || !bookingData.selectedDateTime || !bookingData.customer) {
      setError('Missing required booking information. Please go back and complete all steps.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const employeeId = bookingData.employeeId || bookingData.selectedEmployee.employeeId;
      const employeeName = bookingData.employeeName || bookingData.selectedEmployee.actualEmployeeName || bookingData.selectedEmployee.name;

      const result = await confirmStaffManualBooking({
        companyId: bookingData.companyData.id,
        customer: bookingData.customer,
        selectedService: bookingData.selectedService,
        selectedEmployee: bookingData.selectedEmployee,
        employeeId,
        employeeName,
        selectedDateTime: bookingData.selectedDateTime.toISOString(),
        notes,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        locationId: bookingData.locationId,
        locationName: bookingData.selectedLocation?.name || '',
        locationAddress: bookingData.selectedLocation?.address || null,
      });

      const customerId = result.customerId || bookingData.customer.id;

      if (bookingData.customer.isNew && customerId) {
        updateBookingData({
          customer: {
            ...bookingData.customer,
            id: customerId,
            isNew: false,
          },
        });
      }

      const storefrontUrl = buildStorefrontShareUrl();
      const successPayload = {
        bookingData: {
          ...bookingData,
          customer: {
            ...bookingData.customer,
            id: customerId,
          },
        },
        shareableLink: storefrontUrl || '',
      };

      setShareableLink(storefrontUrl || '');
      setBookingConfirmed(true);
      setLoading(false);

      if (storefrontUrl && result.shortSlug) {
        void tryCreateShortLink(result.shortSlug, storefrontUrl).then((shortUrl) => {
          if (shortUrl && shortUrl !== storefrontUrl) {
            setShareableLink(shortUrl);
          }
        });
      }

      if (onSuccess) {
        onSuccess(successPayload);
        return;
      }
    } catch (err) {
      console.error('[ConfirmationStep] Error confirming booking:', err);
      const message = err?.message || 'Failed to confirm booking. Please try again.';
      setError(message);
      showToast(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !bookingConfirmed) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (bookingConfirmed) {
    return (
      <Box>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.success.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <CheckCircleIcon
              sx={{
                fontSize: 40,
                color: theme.palette.success.main,
              }}
            />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
            Booking Confirmed
          </Typography>
          <Typography variant="body1" color="text.secondary">
            The appointment has been successfully scheduled
          </Typography>
        </Box>

        {shareableLink ? (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              mb: 3,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                mb: 1,
                color: 'text.primary',
              }}
            >
              Share Appointment Details
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              Share this link with your customer to view their appointment details:
            </Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 2,
                bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.5) : alpha(theme.palette.grey[50], 0.5),
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
                mb: 2.5,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontFamily: 'monospace',
                  color: 'text.primary',
                  fontSize: '0.875rem',
                }}
              >
                {shareableLink}
              </Typography>
              <Tooltip title="Copy Link">
                <IconButton size="small" onClick={handleCopyLink} sx={{ ml: 1 }}>
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={emailCooldownLabel ? `Try again in ${emailCooldownLabel}` : sendingEmail ? 'Sending...' : 'Email to Customer'}>
                <IconButton size="small" onClick={handleEmailLink} disabled={sendingEmail || !!emailCooldownLabel}>
                  <EmailIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                <GradientButton
                  variant="primary"
                  onClick={handleEmailLink}
                  startIcon={<EmailIcon />}
                  disabled={sendingEmail || !!emailCooldownLabel}
                >
                  {sendingEmail ? 'Sending…' : 'Email to Customer'}
                </GradientButton>
                {emailCooldownLabel && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Try again in {emailCooldownLabel}
                  </Typography>
                )}
              </Box>
              <GradientButton variant="success" onClick={handleCopyLink} startIcon={<CopyIcon />}>
                Copy Link
              </GradientButton>
            </Box>
          </Paper>
        ) : (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              mb: 3,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Set up your website name in Client Experience settings to share a booking link with customers.
            </Typography>
          </Paper>
        )}

      </Box>
    );
  }

  // Calculate total price for display
  const basePrice = bookingData.selectedService?.selectedPackage?.price || bookingData.selectedService?.price || 0;
  const addonsTotal = (bookingData.selectedService?.selectedAddons || []).reduce((sum, addon) => sum + (addon.price || 0), 0);
  const totalPrice = basePrice + addonsTotal;

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 600,
          mb: 3,
          color: 'text.primary',
          letterSpacing: '-0.02em',
          textAlign: 'center',
        }}
      >
        Booking Summary
      </Typography>

      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {/* Customer Information Section */}
        <Box
          sx={{
            p: 3,
            borderBottom: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.5) : alpha(theme.palette.grey[50], 0.5),
          }}
        >
          <Typography
            variant="overline"
            sx={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: 'primary.main',
              letterSpacing: '0.08em',
              mb: 2,
              display: 'block',
            }}
          >
            Customer Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.8125rem' }}>
                Name
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {bookingData.customer.name}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.8125rem' }}>
                Email
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {bookingData.customer.email}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.8125rem' }}>
                Phone
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {formatPhoneNumber(bookingData.customer.phone)}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Service & Staff Section */}
        <Grid container>
          <Grid item xs={12} sm={6} sx={{ borderRight: { sm: `1px solid ${theme.palette.divider}` } }}>
            <Box sx={{ p: 3 }}>
              <Typography
                variant="overline"
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: 'primary.main',
                  letterSpacing: '0.08em',
                  mb: 2,
                  display: 'block',
                }}
              >
                Service
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 1.5 }}>
                {bookingData.selectedService?.name || 'No service selected'}
              </Typography>
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem', mb: 0.5 }}>
                  Duration
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {bookingData.selectedService?.duration || 0} minutes
                </Typography>
              </Box>
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem', mb: 0.5 }}>
                  Base Price
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.main' }}>
                  ${basePrice.toFixed(2)}
                </Typography>
              </Box>
              {bookingData.selectedService?.selectedAddons?.length > 0 && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem', mb: 0.5 }}>
                    Add-ons
                  </Typography>
                  {bookingData.selectedService.selectedAddons.map((addon, idx) => (
                    <Typography key={idx} variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                      {addon.name} <Box component="span" sx={{ color: 'success.main' }}>(+${addon.price?.toFixed(2) || 0})</Box>
                    </Typography>
                  ))}
                </Box>
              )}
              {bookingData.selectedService?.selectedPackage && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem', mb: 0.5 }}>
                    Package
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {bookingData.selectedService.selectedPackage.name}
                  </Typography>
                </Box>
              )}
              {totalPrice > basePrice && (
                <Box
                  sx={{
                    mt: 2,
                    pt: 2,
                    borderTop: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem', mb: 0.5 }}>
                    Total Price
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main' }}>
                    ${totalPrice.toFixed(2)}
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ p: 3 }}>
              <Typography
                variant="overline"
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: 'primary.main',
                  letterSpacing: '0.08em',
                  mb: 2,
                  display: 'block',
                }}
              >
                Staff Member
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {bookingData.selectedEmployee.name}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Date & Time Section */}
        <Box
          sx={{
            p: 3,
            borderTop: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.5) : alpha(theme.palette.grey[50], 0.5),
          }}
        >
          <Typography
            variant="overline"
            sx={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: 'primary.main',
              letterSpacing: '0.08em',
              mb: 2,
              display: 'block',
            }}
          >
            Date & Time
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.8125rem' }}>
                Date
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {format(bookingData.selectedDateTime, 'EEEE, MMMM d, yyyy')}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.8125rem' }}>
                Time
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {format(bookingData.selectedDateTime, 'h:mm a')}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Notes Section */}
        <Box
          sx={{
            p: 3,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography
            variant="overline"
            sx={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: 'primary.main',
              letterSpacing: '0.08em',
              mb: 2,
              display: 'block',
            }}
          >
            Booking Notes
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={notes}
            onChange={handleChangeNotes}
            placeholder="Add any special instructions or notes for this booking..."
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                bgcolor: 'background.paper',
                '& fieldset': {
                  borderColor: theme.palette.divider,
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.text.secondary,
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.palette.primary.main,
                  borderWidth: 1,
                },
              },
            }}
          />
        </Box>
      </Paper>

      <Box
        sx={{
          mt: 4,
          display: 'flex',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <GradientButton
          onClick={onBack}
          variant="primary"
          sx={{
            minWidth: 120,
          }}
        >
          Back
        </GradientButton>
        <GradientButton
          variant="success"
          onClick={handleConfirmBooking}
          disabled={loading}
          sx={{
            minWidth: 160,
          }}
        >
          {loading ? 'Confirming...' : 'Confirm Booking'}
        </GradientButton>
      </Box>

      {error && (
        <Box
          sx={{
            mt: 3,
            p: 2.5,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.error.main, 0.1),
            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.error.main, 0.2),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <CheckIcon sx={{ color: 'error.main', transform: 'rotate(45deg)' }} />
          </Box>
          <Typography color="error" sx={{ fontWeight: 500 }}>
            {error}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ConfirmationStep; 